if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.syncThumbnailsFromMainSlides();
        this.bindViewerSlideSync();
        this.bindThumbnailClicks();

        // Re-sync after layout so image srcs / order are final
        requestAnimationFrame(() => {
          this.syncThumbnailsFromMainSlides();
          this.bindThumbnailClicks();
        });

        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      /**
       * Build thumbnail-slider from main .product__media-list slides
       * in the same left-to-right order (1st → 2nd → 3rd…).
       */
      syncThumbnailsFromMainSlides() {
        const mainList = this.elements.viewer?.querySelector('.product__media-list');
        if (!mainList) return;

        let mainSlides = Array.from(mainList.children).filter((slide) => {
          if (!slide?.dataset?.mediaId) return false;
          if (slide.classList.contains('product__media-item--variant')) {
            const style = window.getComputedStyle(slide);
            if (style.display === 'none') return false;
          }
          return true;
        });

        // Enforce order by data-gallery-index (1, 2, 3…) then DOM position
        mainSlides.sort((a, b) => {
          const ai = parseInt(a.dataset.galleryIndex || '0', 10);
          const bi = parseInt(b.dataset.galleryIndex || '0', 10);
          if (ai !== bi) return ai - bi;
          return 0;
        });

        if (!mainSlides.length) return;

        // Single visible image: no thumbs, no arrows
        if (mainSlides.length <= 1) {
          this.classList.add('media-gallery--single');
          this.dataset.mediaCount = '1';
          if (this.elements.thumbnails) {
            this.elements.thumbnails.classList.add('hidden');
          }
          this.elements.viewer?.querySelectorAll('.preview_btn_d, .next_btn_d').forEach((el) => {
            el.style.display = 'none';
          });
          this.elements.viewer?.querySelector('.slider-buttons')?.classList.add('hidden');
          return;
        }

        let thumbSlider = this.elements.thumbnails;
        if (!thumbSlider) {
          thumbSlider = this.createThumbnailSlider();
          this.elements.thumbnails = thumbSlider;
        }
        if (!thumbSlider) return;

        this.classList.remove('media-gallery--single');
        this.dataset.mediaCount = String(mainSlides.length);
        thumbSlider.classList.remove('hidden');
        thumbSlider.classList.remove('thumbnail-slider--no-slide');
        this.elements.viewer?.querySelectorAll('.preview_btn_d, .next_btn_d').forEach((el) => {
          el.style.display = '';
        });

        const thumbList = thumbSlider.querySelector('.thumbnail-list') || thumbSlider.querySelector('ul');
        if (!thumbList) return;

        const viewerId = this.elements.viewer?.id || '';
        thumbList.innerHTML = '';
        thumbList.style.flexDirection = 'row';
        thumbList.style.justifyContent = 'flex-start';

        mainSlides.forEach((slide, index) => {
          const mediaId = slide.dataset.mediaId;
          // Prefer real product image — never the variant overlay (.Myid)
          const img =
            slide.querySelector('.product__modal-opener img:not(.Myid)') ||
            slide.querySelector('.product__media img:not(.Myid)') ||
            slide.querySelector('.media img:not(.Myid)') ||
            slide.querySelector('img:not(.Myid)');
          if (!mediaId || !img) return;

          const src =
            img.getAttribute('src') ||
            img.getAttribute('data-src') ||
            img.currentSrc ||
            '';
          const alt = img.getAttribute('alt') || '';
          const position = parseInt(slide.dataset.galleryIndex || String(index + 1), 10);

          const li = document.createElement('li');
          li.id = `Slide-Thumbnails-${mediaId}`;
          li.className = 'thumbnail-list__item slider__slide';
          li.dataset.target = mediaId;
          li.dataset.mediaPosition = String(position);
          li.dataset.galleryThumb = 'true';
          li.dataset.galleryIndex = String(position);
          li.style.order = String(position);

          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'thumbnail global-media-settings global-media-settings--no-shadow';
          button.setAttribute('aria-label', `Load image ${position}`);
          button.setAttribute('aria-controls', viewerId);
          if (position === 1 || slide.classList.contains('is-active')) {
            button.setAttribute('aria-current', 'true');
          }

          const thumbImg = document.createElement('img');
          thumbImg.src = src;
          thumbImg.alt = alt;
          thumbImg.width = 208;
          thumbImg.height = 208;
          thumbImg.loading = position === 1 ? 'eager' : 'lazy';
          thumbImg.decoding = 'async';

          button.appendChild(thumbImg);
          li.appendChild(button);
          thumbList.appendChild(li);
        });

        // Recompute slider pages after dynamic thumbnail rebuild
        if (typeof thumbSlider.resetPages === 'function') {
          thumbSlider.resetPages();
        }

        // Scroll thumb strip back to the start (first image)
        requestAnimationFrame(() => {
          if (thumbList.scrollLeft) thumbList.scrollLeft = 0;
          if (thumbSlider.slider) thumbSlider.slider.scrollLeft = 0;
          if (typeof thumbSlider.update === 'function') {
            thumbSlider.update();
          }
        });
      }

      createThumbnailSlider() {
        if (!this.elements.viewer) return null;

        const sectionMatch = this.id?.match(/^MediaGallery-(.+)$/);
        const sectionId = sectionMatch ? sectionMatch[1] : 'gallery';

        const slider = document.createElement('slider-component');
        slider.id = `GalleryThumbnails-${sectionId}`;
        slider.className = 'thumbnail-slider slider-mobile-gutter quick-add-hidden';

        slider.innerHTML = `
          <button type="button" class="slider-button slider-button--prev" name="previous" aria-label="Previous" data-step="3">
            <span class="svg-wrapper"></span>
          </button>
          <ul id="Slider-Thumbnails-${sectionId}" class="thumbnail-list list-unstyled slider slider--mobile slider--tablet-up"></ul>
          <button type="button" class="slider-button slider-button--next" name="next" aria-label="Next" data-step="3">
            <span class="svg-wrapper"></span>
          </button>
        `;

        this.elements.viewer.after(slider);
        return slider;
      }

      bindThumbnailClicks() {
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const btn = mediaToSwitch.querySelector('button');
          if (!btn || btn.dataset.bound === 'true') return;
          btn.dataset.bound = 'true';
          btn.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
      }

      bindViewerSlideSync() {
        if (!this.elements.viewer) return;

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 150)
        );
      }

      onSlideChanged(event) {
        const current = event?.detail?.currentElement;
        if (!current) return;

        window.__productGalleryMode = true;
        this.querySelectorAll('.product__media .Myid, .custom_media .Myid').forEach((el) => el.remove());

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === current);
        });

        if (!this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${current.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (!this.elements.viewer || !mediaId) return false;

        const exactMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!exactMedia) return false;

        const activeMedia = exactMedia;

        window.__productGalleryMode = true;
        this.querySelectorAll('.product__media .Myid, .custom_media .Myid').forEach((el) => el.remove());

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          if (activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }
          // Do not prepend thumbnails — keep 1st → 2nd → 3rd order intact
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return true;

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
        return true;
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);

        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;
        this.elements.thumbnails.slider?.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion || !window.accessibilityStrings?.imageAvailable) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
            '[index]',
            position
          );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer?.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems?.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
